// The system says what it did. Spec 002 contracts/shared-fixes.md §4.
//
// AGE ships the atom — `mud-toast`, documented as "semantic toast message
// (350px filled surface)", with the live-region routing already correct
// (`role=status` + polite for info/success, `role=alert` + assertive for
// warning/error) — and explicitly does **not** position, stack or auto-dismiss
// it: "the consumer is responsible for removing the notification from the DOM".
// SIMSM owns exactly those three things and nothing else, so no token is declared
// and R2 is not engaged.
//
// Mounted once, in the application shell. Call sites use
// `const toast = useToast()` then `toast({ variant, title, text })`; the text is
// always a Tolgee-resolved string, never a raw server message. This library
// carries no translation runtime on purpose — every string reaching it has
// already been resolved by the caller, which is why the close label arrives as a
// prop rather than being looked up here.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';
import { MudToast } from '@egov-moldova/mud-react';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastInput {
  variant: ToastVariant;
  /** Bold heading — the wireframe's `titlu`. */
  title: string;
  /**
   * Optional second line — the wireframe's `text`, usually the object's name.
   * Explicitly `| undefined` because callers routinely compute it from data that
   * may not be loaded, and the workspace runs `exactOptionalPropertyTypes`.
   */
  text?: string | undefined;
  /**
   * Accessible label for the close button, in the interface language. Host
   * chrome rather than message content, so it normally comes from the
   * provider's own `closeLabel`; a call site only passes it to override one
   * toast. When neither is supplied the atom keeps its own documented default.
   */
  closeLabel?: string | undefined;
}

interface Toast extends ToastInput {
  id: number;
}

export type ToastFn = (input: ToastInput) => void;

export interface ToastProviderProps {
  /**
   * Accessible label for every toast's close button, resolved once by the
   * caller in the interface language. Omitted only by tests and by callers that
   * accept the atom's own default.
   */
  closeLabel?: string | undefined;
}

const ToastContext = createContext<ToastFn | null>(null);

/** A success or an info toast has been read by the time it matters; a warning or
 *  an error is the one the reader has to act on, so it waits for them. */
const AUTO_DISMISS_MS: Record<ToastVariant, number | null> = {
  success: 5000,
  info: 6000,
  warning: null,
  error: null,
};

/** Newest sits nearest the corner. Beyond this the oldest is dropped rather than
 *  stacked off-screen, where nobody would read it anyway. */
const MAX_VISIBLE = 3;

export function useToast(): ToastFn {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast must be used within ToastProvider');
  return toast;
}

export function ToastProvider({
  children,
  closeLabel,
}: PropsWithChildren<ToastProviderProps>): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastFn>(
    (input) => {
      const id = nextId.current;
      nextId.current += 1;
      setToasts((current) => [{ ...input, id }, ...current].slice(0, MAX_VISIBLE));
      const after = AUTO_DISMISS_MS[input.variant];
      if (after !== null) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), after),
        );
      }
    },
    [dismiss],
  );

  // One owner for every pending timer, so a toast that leaves the stack by any
  // route takes its timer with it. Dismissal is not the only such route: the
  // MAX_VISIBLE cap drops the oldest silently, and a timer left behind by that
  // drop used to survive until it fired against an id nobody held any more.
  const pending = timers.current;
  useEffect(() => {
    const live = new Set(toasts.map((t) => t.id));
    for (const [id, timer] of pending) {
      if (!live.has(id)) {
        clearTimeout(timer);
        pending.delete(id);
      }
    }
  }, [toasts, pending]);

  useEffect(
    () => () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    },
    [pending],
  );

  const host = useMemo(
    () =>
      toasts.length === 0
        ? null
        : createPortal(
            <div className="simsm-toast-host">
              {toasts.map((t) => {
                const label = t.closeLabel ?? closeLabel;
                return (
                  <MudToast
                    key={t.id}
                    variant={t.variant}
                    titleText={t.title}
                    closable
                    // Never a hardcoded fallback: an unresolved label would be one
                    // language's word shown to every reader. Absent, the atom's own
                    // default applies and stays AGE's business.
                    {...(label === undefined ? {} : { closeLabel: label })}
                    onMudClose={() => dismiss(t.id)}
                  >
                    {t.text}
                  </MudToast>
                );
              })}
            </div>,
            document.body,
          ),
    [toasts, dismiss, closeLabel],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {host}
    </ToastContext.Provider>
  );
}
